<template>
  <ContainerView class="flex w-full h-full" gradient>
    <LoadingControl v-show="loading" :loading="loading"/>

    <!-- Main Forgot Password Section -->
    <CardView rounded max_width="max-w-xl p-4 lg:p-10 m-auto" v-if="show">
      <div class="w-full">
        <div class="text-base flex flex-col mb-4">
          <img src="https://resource.pafcpic.net/assets/papsi/papsi_logo.png" class="w-1/4 mb-2" />
          <h2 class="text-2xl lg:text-3xl font-bold text-theme-primary mb-2">Forgot Password</h2>
          <p>Please enter any of the following details.</p>
        </div>
        <Textbox type="text"
          id="forgot_value"
          class="mb-4"
          @changed="changed"
          :max_length="23"
          v-bind:value="forgot_value"
          placeholder="Username, Email Address, Mobile Number, Account or Serial No."
          required />
        
        <!-- Captcha Section -->
        <div class="pb-2 flex justify-center">
          <div>
            <!-- Show Recaptcha if loaded -->
            <VueRecaptcha
              v-if="recaptchaLoaded !== false"
              @verify="captchaVerify"
              @expired="resetCaptcha"
              :sitekey="key"
              size="32"
              class="w-full"
            />


            <!-- Fallback message -->
            <div
              v-if="recaptchaLoaded === false"
              class="text-red-500 bg-red-100 px-4 py-2 rounded text-sm text-center"
            >
              ReCAPTCHA is not loaded. Please refresh this page.
            </div>
          </div>
        </div>

        <div class="flex flex-row space-x-3 justify-center mt-2">
          <Button @clicked="back" is_secondary rounded :button_text="lang('BACK')"/>
          <Button @clicked="submit"  :theme="'text-white'" v-bind:disabled="disable_button" rounded :button_text="lang('CONTINUE')"/>
        </div>
      </div>
    </CardView>

    <!-- Account Verification Section -->
    <CardView rounded max_width="max-w-xl p-4 lg:p-10 m-auto" v-if="show_identifier">
      <div class="w-full">
        <img src="https://resource.pafcpic.net/assets/papsi/papsi_logo.png" class="w-1/4 mb-2" />
        <h2 class="text-2xl lg:text-3xl font-bold text-theme-primary mb-2">Verify your Account</h2>
        <p class="mb-4">Please verify your user information below to continue</p>
        <div class="flex flex-col bg-gray-100 rounded-lg p-5">
          <div class="text-sm">
            <div class="flex flex-row">
              <div class="p-2 font-bold w-full uppercase">USERNAME</div>
              <div class="p-2 text-gray-500" v-text="name"></div>
            </div>
            <div class="flex flex-row">
              <div class="p-2 font-bold w-full uppercase">EMAIL ADDRESS</div>
              <div class="p-2 text-gray-500">{{ maskEmail(email) }}</div>
            </div>
            <div class="flex flex-row">
              <div class="p-2 font-bold w-full uppercase">MOBILE NUMBER</div>
              <div class="p-2 text-gray-500">{{ maskMobile(mobile) }}</div>
            </div>
          </div>
        </div>

        <!-- Error Message Display -->
        <div
          v-if="isMissingContactInfo"
          class="w-full flex items-center justify-center text-center mt-4"
        >
          <div class="text-red-500 text-left bg-red-100 px-4 py-2 rounded-xl font-medium">
            You don't have Email Address and Mobile Number. Please visit to your nearest PSO branch to update your contact information.
          </div>
        </div>

        <!-- Buttons -->
        <div class="flex flex-row space-x-3 justify-center pt-4">
          <Button @clicked="remove" is_secondary :button_text="isMissingContactInfo ? 'BACK' : lang('NOT ME')" />
          
          <Button
            @clicked="proceed"
            :theme="'text-white'"
            :button_text="lang('PROCEED')"
            :disabled="isMissingContactInfo"
          />
        </div>


      </div>
    </CardView>

    <!-- OTP Validation Section -->
    <CardView rounded max_width="max-w-xl p-4 lg:p-10 m-auto" v-if="show_otp_modal">
      <div class="w-full">
        <img src="https://resource.pafcpic.net/assets/papsi/papsi_logo.png" class="w-1/4 mb-2" />
        <h2 class="text-2xl lg:text-3xl font-bold text-theme-primary mb-2 text-left">Validate OTP</h2>
        <p>Please enter the One-Time Password (OTP) we have sent to your Email Address or Mobile Number to verify your identity.</p>
        <div class="pb-4 w-full justify-items-center font-bold uppercase text-left mt-4">
              <v-otp-input
                :is-disabled="false"
                ref="otp_input"
                input-classes="otp-input"
                :key="otpInputKey"
                :conditionalClass="['1', '2', '3', '4', '5', '6']"
                @on-complete="submitOtp"
                @on-change="changeOTPButton"
                separator=" "
                :input-type="'number'"
                inputmode="numeric"
                :num-inputs="6"
                v-model:value="otp"
                :should-auto-focus="true"
                :should-focus-order="true"
              />
        </div>
        
        <div class="flex flex-row pb-3 gap-2 w-full justify-center text-center text-gray-400 text-sm">
          <p>Didn't receive a code? <span v-show="!show_resend_otp" class="font-semibold">Resend in {{ formattedTimer }}</span></p>
          <LinkText emit_triggered='resendOtp' @resendOtp="resendOtp" class="text-theme-primary font-bold" text="Resend" v-show="show_resend_otp"/>
        </div>
        <!-- Error Message Display -->
        <div v-show="otp_error_message" class="w-full flex items-center justify-center text-center my-3 rounded-full">
                        <div class="text-red-500 text-center bg-red-100 px-4 py-2 rounded-full font-medium" 
                        v-text="otp_error_message" 
                    />
                    </div>
        <div class="flex flex-row space-x-3 justify-center pt-3">
          <Button @clicked="remove" is_secondary :button_text="lang('BACK')"/>
          <!-- <Button @clicked="submitOtp" :theme="'text-white'" :button_text="lang('SUBMIT')"  v-bind:disabled="disable_otpbutton"/> -->
        </div>
      </div>
    </CardView>

    <!-- Message Modal (Success or Error) -->
    <Modal v-show="show_message_modal" 
      v-bind:display="show_message_modal"
      :error="error"
      :success="success"
      :note="warning" 
      @positiveClick="remove"
      :positiveButton="'OK'"
      :z_value="'z-50'"
      :default_width="'max-w-xl'"
      id='message_modal'>
      <div class="w-full px-5 pt-2 pb-4 flex flex-col text-xl justify-center text-center items-center">
        {{ this.modal_message }}
      </div>
    </Modal>

  </ContainerView>
</template>


<script>
import { defineComponent } from "vue";
import { VueRecaptcha } from "vue-recaptcha";
import Button from "@/components/controls/ButtonControl.vue";
import Modal from "@/components/controls/ModalControl.vue";
import Textbox from "@/components/controls/TextBoxField.vue";
import ContainerView from "@/components/sections/ContainerView.vue";
import User from  "@/services/User";
import mixin from "@/mixin";
import OTP from "@/services/OTP";
// import Icon from "@/components/controls/Icon.vue";
import LoadingControl from "@/components/controls/LoadingControl.vue";
import CardView from "@/components/controls/CardView.vue";
import LinkText from "@/components/controls/LinkText";
import VOtpInput from "vue3-otp-input";

export default defineComponent({
  mixins: [
    mixin,
  ],
  inject: ['setLoading'],
  components:{
    LoadingControl,
    ContainerView,
    VueRecaptcha,
    CardView,
    LinkText,
    Textbox,
    Button,
    Modal,
    VOtpInput,
    // Icon,
  },
  computed: {
    isMissingContactInfo() {
      return (!this.email || this.email.trim() === '') && (!this.mobile || this.mobile.trim() === '');
    },
    formattedTimer() {
      const min = Math.floor(this.timerCount / 60);
      const sec = this.timerCount % 60;
      return `${min}:${sec.toString().padStart(2, '0')}`;
    }
  },
  mounted() {
    this.show = true;

    // Detect if recaptcha script is available
    const checkRecaptcha = setInterval(() => {
      if (window.grecaptcha) {
        this.recaptchaLoaded = true;
        clearInterval(checkRecaptcha);
        clearTimeout(this.recaptchaTimeout);
      }
    }, 500);

    this.recaptchaTimeout = setTimeout(() => {
      if (this.recaptchaLoaded === null) {
        this.recaptchaLoaded = false;
        clearInterval(checkRecaptcha);
      }
    }, 5000);
  },
  data(){
    return {
        key:'6LdTAAQhAAAAAApdt61xDVJaAUU5Cv6opGbLJFFT',
        forgot_value:'',
        captcha:false,
        disable_button:true,
        disable_otpbutton: true,
        show_identifier:false,
        show_message_modal:false,
        show_resend_otp:false,
        show_otp_modal:false,
        name:'',
        pin:'',
        otp:'',
        otpInputKey: 0, 
        otp_error_message:'',
        timerCount:0,
        timerEnabled:false,
        show:false,
        loading:false,
        email:'',
        modal_message:'',
        mobile:'',
        error:false,
        success:false,
        warning:false,
        fade:false,
        recaptchaLoaded: null,
        recaptchaTimeout: null,
    }
  },
  methods:{
    changed(e) {
      let key = e.key;
      this[key] = e.value;
      this.enableButton();
    },
    back() {
      this.$router.push('/');
    },
    // otpUpdate(e) {
    //   this.otp = e.value;
    // },
    submitOtp() {
      // this.loading = true;
      const that = this;
      that.setLoading(true);
      let store = this.$store;
      let state = store.state.recovery;
      OTP.validate({ otp: this.otp, type: 1, pin: state.pin })
        .then((response) => {
          this.show_otp_modal = false;
          state.otp_token = response.data.data.otp_token;
          state.otp_pin = response.data.data.otp_pin;
          that.setLoading(false);
          this.$router.push('/forgot-password/update');
        })
        .catch((error) => {
          const that = this;
          // this.error = true;
          // this.loading = false;
          // this.show_message_modal = true;
          // this.modal_message = error.response.data.message;
          that.otp_error_message = error.response.data.message;
          // this.loading = false;
          that.otp = '';
          that.otpInputKey++; 
          that.setLoading(false);

        });
    },
    resendOtp() {
      // this.loading = true;
      this.show_resend_otp = false;
      this.timerEnabled = true;
      this.timerCount = 120;
      OTP.requestOtp({
        type: 1,
        email: this.email,
        mobile: this.mobile,
        name: this.name,
        pin: this.$store.state.recovery.pin,
      })
        .then((response) => {
          this.pin = response.data.data.pin;
          // this.loading = false;
          this.show_otp_modal = true;
        })
        .catch(() => {
          // this.loading = false;
        });
    },
    submit() {
      // this.loading = true;
      const that = this;
      that.setLoading(true);
      const store = this.$store;
      const state = store.state.forgot_password_payload;

      const numericValue = this.forgot_value.replace(/\D/g, '');
      if (/^09\d{8,}$/.test(numericValue)) {
        this.forgot_value = numericValue.replace(/^0/, '+63');
      }

      User.forgotPassword(this.forgot_value)
        .then((response) => {
          const that = this;
          let user_data = response.data.data;
          this.name = user_data.username;
          this.email = user_data.email;
          this.mobile = user_data.mobile_no;
          this.pin = user_data.pin;
          this.show = false;
          this.show_identifier = true;
          state.uuid = user_data.uuid;
          state.hash = user_data.hash;
          state.pin = user_data.pin;
          // this.loading = false;
          that.setLoading(false);
        })
        .catch((error) => {
          const that = this;
          this.error = true;
          this.show_message_modal = true;
          this.modal_message = error.response.data.message;
          // this.loading = false;
          that.setLoading(false);
        });
    },
    proceed() {
      // this.loading = true;
      const that = this;
      that.setLoading(true);
      const store = this.$store;
      const state = store.state.forgot_password_payload;
      this.show_identifier = false;
      User.recovery(state, this.forgot_value)
        .then((response) => {
          const that = this;
          store.state.recovery.hash = response.data.data.hash;
          store.state.recovery.pin = response.data.data.pin;
          this.timerEnabled = true;
          this.timerCount = 120;
          // this.loading = false;
          that.setLoading(false);
          this.show_otp_modal = true;
        })
        .catch((error) => {
          const that = this;
          this.error = true;
          this.show_message_modal = true;
          this.modal_message = error.response.data.message;
          // this.loading = false;
          that.setLoading(false);
        });
    },
    remove() {
      this.show_message_modal = false;
      this.otp_error_message = '';
      this.show_identifier = false;
      this.show_otp_modal = false;
      this.show = true;
      this.disable_button = true;
    },
    captchaVerify() {
      this.captcha = true;
      this.recaptchaLoaded = true;
      this.enableButton();
    },
    resetCaptcha() {
      this.captcha = false;
      this.enableButton();
    },
    enableButton() {
      this.disable_button = !(this.forgot_value !== '' && this.captcha);
    },
    enableOTPButton() {
      this.disable_otpbutton = false;
    },
    changeOTPButton() {
      this.disable_otpbutton = true;
      this.otp_error_message = '';
    },
    maskEmail(email) {
      if (!email) return '';
      const [user, domain] = email.split('@');
      if (user.length <= 3) {
        // If username is short, mask all but the first character
        return user[0] + '*'.repeat(Math.max(2, user.length - 1)) + '@' + domain;
      }
      // Mask randomly, but always at least 3 chars
      let maskCount = Math.max(3, Math.floor(user.length * 0.6));
      let indices = [];
      while (indices.length < maskCount) {
        let idx = Math.floor(Math.random() * (user.length - 1)) + 1; // don't mask first char
        if (!indices.includes(idx)) indices.push(idx);
      }
      let masked = user.split('').map((c, i) => (i === 0 || !indices.includes(i) ? c : '*')).join('');
      return masked + '@' + domain;
    },
    maskMobile(mobile) {
      if (!mobile) return '';
      if (mobile.length <= 7) return mobile; // Not enough length to mask

      let start = mobile.slice(0, 3);               // First 3 digits
      let end = mobile.slice(-4);                   // Last 4 digits
      let middle = '*'.repeat(mobile.length - 7);   // Mask the in-between part

      return start + middle + end;
    }

  },
   watch:{
    show() {
          setTimeout(() => {
            this.fade=true;
        }, 100);
    },
    timerCount: {
      handler(value) {
        if (value > 0 && this.timerEnabled) {
          this.show_resend_otp = false;
          setTimeout(() => {
            this.timerCount = this.timerCount - 1;
            // console.log(this.timerCount);
          }, 1000);
        } else {
          this.show_resend_otp = true;
          this.timerEnabled = false; // run your function here
        }
      },
      immediate: true
    },
   }
});
</script>
<style scoped>
.contact-w {
    width: 100px !important;
    min-width: 120px !important;
}
</style>